import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { DATE_FORMATS_FNS, DetailsCard } from "components";
import { DealerQualityChecksTable, DealerQualityChecksTabularFilters } from "modules/DealerReports/components/DealerQualityChecksTabular/components";
import "modules/DealerReports/components/DealerQualityChecksTabular/DealerQualityChecksTabular.scss";
import { questionStatusLookup } from "modules/DealerReports/components/DealerQualityChecksTabular/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { QUESTION_RESULT_STATUSES } from "models";

export type QualityCheckReportData = {
  wo_nr: string;
  wo_date: string;
  location: string;
  mechanic: string;
  qc_status: number;
  qcChecklist: string;
  check_date: string;
  qc_officer: string;
  mechanic_id: number;
  qc_checklist: string;
  qc_officer_id: number;
  advised_remark: string;
  appointment_id: number;
  critical_remark: string;
  ok_with_note_remark: string;
} & Record<string, unknown>;

type FilterOption = { id: number; name: string };

export type QualityCheckReportPayload = {
  rows: QualityCheckReportData[] | null;
  filters: {
    mechanics: FilterOption[];
    qc_officers: FilterOption[];
    dealer_locations: FilterOption[];
    quality_checklists: FilterOption[];
  };
};

type Props = {
  id: string;
  title: string | undefined;
  isExpanded: boolean;
  data: QualityCheckReportPayload;
  reportName: string;
  handleExportData: (reportName: string, reportData: Record<string, unknown>[]) => void;
  mechanicId?: number;
  locationId?: number;
  qcOfficerId?: number;
  qcStatus?: QUESTION_RESULT_STATUSES;
  qcChecklistId?: number;
  setMechanicId: (value?: number) => void;
  setLocationId: (value?: number) => void;
  setQcOfficerId: (value?: number) => void;
  setQcStatus: (value?: QUESTION_RESULT_STATUSES) => void;
  setQcChecklistId: (value?: number) => void;
  isFetching?: boolean;
  isExporting?: boolean;
};

export const DealerQualityChecksTabular = ({
  handleExportData,
  id,
  isExpanded,
  data,
  reportName,
  title,
  mechanicId,
  locationId,
  qcOfficerId,
  qcStatus,
  qcChecklistId,
  setMechanicId,
  setLocationId,
  setQcOfficerId,
  setQcStatus,
  setQcChecklistId,
  isFetching,
  isExporting
}: Props) => {
  const { t } = useTranslation();

  const { rows, filters } = data;
  const { mechanics, qc_officers, dealer_locations, quality_checklists } = filters;

  const handleExport = () => {
    const exportRows = (rows ?? []).map(row => {
      const qcStatus = questionStatusLookup.find(({ key }) => key === row.qc_status);

      return {
        location: row.location,
        wo_date: row.wo_date ? formatDate(row.wo_date, DATE_FORMATS_FNS.dateMonthYear) : "",
        wo_nr: row.wo_nr,
        check_date: row.check_date ? formatDate(row.check_date, DATE_FORMATS_FNS.dateMonthYear) : "",
        mechanic: row.mechanic,
        qc_status: qcStatus ? t(qcStatus.translationKey) || qcStatus.defaultMessage : "",
        qc_officer: row.qc_officer,
        qc_checklist: row.qc_checklist,
        ok_with_note_remark: row.ok_with_note_remark,
        advised_remark: row.advised_remark,
        critical_remark: row.critical_remark
      };
    });

    handleExportData(reportName, exportRows);
  };

  return (
    <div className="dealer-quality-checks-tabular">
      <DetailsCard
        sectionMenuId={id}
        title={title}
        isExpanded={isExpanded}
        rightContent={
          <div className="details-card-container">
            <DealerQualityChecksTabularFilters
              mechanics={mechanics}
              qcOfficers={qc_officers}
              dealerLocations={dealer_locations}
              qualityChecklists={quality_checklists}
              mechanicId={mechanicId}
              locationId={locationId}
              qcOfficerId={qcOfficerId}
              qcStatus={qcStatus}
              qcChecklistId={qcChecklistId}
              setMechanicId={setMechanicId}
              setLocationId={setLocationId}
              setQcOfficerId={setQcOfficerId}
              setQcStatus={setQcStatus}
              setQcChecklistId={setQcChecklistId}
            />
            <div className="export-button">
              <Button icon color="green" basic disabled={isExporting} loading={isExporting} onClick={handleExport}>
                <Icon className="file export" />
              </Button>
            </div>
          </div>
        }
      >
        <DealerQualityChecksTable reportData={rows ?? []} isFetching={isFetching} />
      </DetailsCard>
    </div>
  );
};
